import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, switchMap, throwError } from 'rxjs';
import { environment } from 'src/app/environments/environment';
import { CredentialEncryptionService } from './credential-encryption.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  menuType: any;
  isLoggedInVar = false;
  menuView = false;
  isNonAdminActive = false;
  url = environment.apiUrl;
	private modalService = inject(NgbModal);
  constructor(
    private http: HttpClient,
    private route: Router,
    private credentialEncryptionService: CredentialEncryptionService) { }

  addAccessToken(accessToken: string) {
    return sessionStorage.setItem('accessToken', accessToken)
  }

  getAccessToken() {
    return sessionStorage.getItem('accessToken')
  }

  addRefreshToken(refreshToken: string) {
    return sessionStorage.setItem('refreshToken', refreshToken)
  }

  getRefreshToken() {
    return sessionStorage.getItem('refreshToken')
  }
  getUserType() {
    return sessionStorage.getItem('userType')
  }

  isLoggedIn() {
    return !this.getAccessToken()
  }

  logout() {
    sessionStorage.removeItem('currentUser')
    sessionStorage.clear()
    this.isLoggedInVar = false;
    this.menuView = false;
    this.menuType = 'public';
    this.modalService.dismissAll()
    this.route.navigate(['secure/login'])
  }

  getUserRole() {
    const helper = new JwtHelperService();
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return null
    }
    const decodedToken = helper.decodeToken(this.getAccessToken() ?? "")
    sessionStorage.setItem('currentUser', JSON.stringify(decodedToken.auth))
    sessionStorage.setItem('userType',decodedToken.userType)
    return decodedToken.auth
  }

  refreshToken() {
    const refreshData = {
      refreshToken: this.getRefreshToken(),
      userType:this.getUserType
      // TODO send me the userType from the auth token
    }
    return this.http.post(this.url + "/ecitation-services/refresh", refreshData)
  }

  /**
   * Encrypts a login password before posting credentials to eCitation Services.
   *
   * @param loginData username and plaintext password from the login form
   * @returns backend login response
   */
  loginUser(loginData: any) {
    const sendLogin = (forceRefresh = false) => this.credentialEncryptionService
      .encryptCredential(loginData.password, forceRefresh)
      .pipe(
        switchMap(credential => this.http.post(this.url + "/ecitation-services/login", {
          username: loginData.username,
          encryptedPassword: credential.encryptedValue,
          keyId: credential.keyId
        }))
      );

    return sendLogin().pipe(
      catchError(error => this.retryWhenCredentialKeyIsStale(error, () => sendLogin(true)))
    )
  }

  /**
   * Encrypts a replacement password before posting it to the password-reset
   * endpoint.
   *
   * @param resetData password-reset token and plaintext replacement password
   * @returns backend password-reset response
   */
  resetPassword(resetData: any) {
    const sendPasswordReset = (forceRefresh = false) => this.credentialEncryptionService
      .encryptCredential(resetData.newPassword, forceRefresh)
      .pipe(
        switchMap(credential => this.http.post(this.url + "/ecitation-services/changePassword", {
          passwordResetToken: resetData.passwordResetToken,
          encryptedNewPassword: credential.encryptedValue,
          keyId: credential.keyId
        }))
      );

    return sendPasswordReset().pipe(
      catchError(error => this.retryWhenCredentialKeyIsStale(error, () => sendPasswordReset(true)))
    )
  }

  /**
   * Retries a credential request once when the backend rejects the encrypted
   * payload, which can happen after key rotation.
   *
   * @param error backend error from the first request
   * @param retryRequest request factory that refreshes key material before retry
   * @returns retry observable or the original error
   */
  private retryWhenCredentialKeyIsStale(error: any, retryRequest: () => any) {
    if (this.isInvalidCredentialPayload(error)) {
      this.credentialEncryptionService.clearCachedKey();
      return retryRequest();
    }

    return throwError(() => error);
  }

  /**
   * Detects backend invalid credential payload responses without exposing
   * credential material in logs or error messages.
   *
   * @param error HTTP error response
   * @returns true when the error is eligible for one key-refresh retry
   */
  private isInvalidCredentialPayload(error: any) {
    const errorText = `${error?.error?.detail ?? ''} ${error?.error?.message ?? ''} ${error?.message ?? ''}`;
    return error?.status === 400 && errorText.includes('Invalid credential payload');
  }
}
